export type Role =
  | 'owner'
  | 'manager'
  | 'accounting'
  | 'sales'
  | 'production'
  | 'staff';

export const ROLES: Role[] = [
  'owner',
  'manager',
  'accounting',
  'sales',
  'production',
  'staff',
];

export const ROLE_LABELS: Record<Role, string> = {
  owner:      'Owner',
  manager:    'Manager',
  accounting: 'Accounting',
  sales:      'Sales',
  production: 'Production',
  staff:      'Staff',
};

export type Module =
  | 'dashboard'
  | 'clients'
  | 'quotes'
  | 'orders'
  | 'production'
  | 'inventory'
  | 'financials'
  | 'accounting'
  | 'newsletter'
  | 'hr'
  | 'reports'
  | 'settings';

/**
 * Permission matrix — both the React router and Firestore rules read
 * from this single source of truth.
 *
 * Values:
 *   'rw'   — read & write
 *   'r'    — read-only
 *   false  — hidden / denied
 */
export const PERMISSIONS: Record<Role, Record<Module, 'rw' | 'r' | false>> = {
  owner: {
    dashboard: 'rw', clients: 'rw', quotes: 'rw', orders: 'rw',
    production: 'rw', inventory: 'rw', financials: 'rw',
    accounting: 'rw', newsletter: 'rw', hr: 'rw', reports: 'rw', settings: 'rw',
  },
  manager: {
    dashboard: 'rw', clients: 'rw', quotes: 'rw', orders: 'rw',
    production: 'rw', inventory: 'rw', financials: 'r',
    accounting: 'r', newsletter: 'rw', hr: 'r', reports: 'rw', settings: 'r',
  },
  accounting: {
    dashboard: 'rw', clients: 'r', quotes: 'r', orders: 'r',
    production: 'r', inventory: 'r', financials: 'rw',
    accounting: 'rw', newsletter: 'r', hr: 'r', reports: 'rw', settings: false,
  },
  sales: {
    dashboard: 'rw', clients: 'rw', quotes: 'rw', orders: 'rw',
    production: 'r', inventory: 'r', financials: false,
    accounting: false, newsletter: 'rw', hr: false, reports: 'r', settings: false,
  },
  production: {
    dashboard: 'rw', clients: 'r', quotes: 'r', orders: 'r',
    production: 'rw', inventory: 'rw', financials: false,
    accounting: false, newsletter: false, hr: false, reports: 'r', settings: false,
  },
  staff: {
    dashboard: 'r', clients: false, quotes: false, orders: false,
    production: false, inventory: false, financials: false,
    accounting: false, newsletter: false, hr: false, reports: false, settings: false,
  },
};

export type AppUser = {
  uid: string;
  email: string;
  displayName: string | null;
  photoURL: string | null;
  role: Role;
  department?: string;
  disabled?: boolean;
  createdAt?: string;
};

export function can(role: Role, mod: Module, action: 'read' | 'write' = 'read') {
  const p = PERMISSIONS[role][mod];
  if (p === false) return false;
  if (action === 'read') return p === 'r' || p === 'rw';
  return p === 'rw';
}
